import { axiosDefaultInstance } from '@/http/default'
import type {
  BillingSummary,
  ChangePlanPreview,
  KargoBill,
  SeatChangePreview,
  SubscriptionPlan
} from '@/model/Billing'
import { normalizeBillingSummary, normalizeChangePlanPreview, normalizeKargoBill, normalizeSeatChangePreview } from '@/model/Billing'
import { useProfileStore } from '@/store/profile.module'
import type { AxiosInstance } from 'axios'
import handleError from './util/ErrorHandler'
import type { ServiceError } from './util/ServiceError'

class BillingService {
  private axiosInstance: AxiosInstance

  constructor() {
    this.axiosInstance = axiosDefaultInstance
  }

  private get companyId(): string {
    return useProfileStore().selectedCompany.id
  }

  private get baseUrl(): string {
    return `${import.meta.env.VITE_API_BASE_URL}/company/${this.companyId}/billing`
  }

  async getSummary(): Promise<BillingSummary | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') || 'es'
      const response = await this.axiosInstance.get<BillingSummary>(`${this.baseUrl}/summary`, {
        params: { locale }
      })
      return normalizeBillingSummary(response.data)
    } catch (error) {
      return handleError(error)
    }
  }

  async getBills(): Promise<KargoBill[] | ServiceError> {
    try {
      const response = await this.axiosInstance.get<KargoBill[]>(`${this.baseUrl}/bills`)
      return response.data.map(normalizeKargoBill)
    } catch (error) {
      return handleError(error)
    }
  }

  async downloadBillPdf(billId: string): Promise<Blob | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') || 'es'
      const response = await this.axiosInstance.get(`${this.baseUrl}/bills/${billId}/pdf`, {
        params: { locale },
        responseType: 'blob'
      })
      return response.data
    } catch (error) {
      return handleError(error)
    }
  }

  async changePlan(plan: SubscriptionPlan): Promise<BillingSummary | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') || 'es'
      const response = await this.axiosInstance.put<BillingSummary>(`${this.baseUrl}/plan`, { plan }, {
        params: { locale }
      })
      return normalizeBillingSummary(response.data)
    } catch (error) {
      return handleError(error)
    }
  }

  async getPlanPreview(plan: SubscriptionPlan): Promise<ChangePlanPreview | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') || 'es'
      const response = await this.axiosInstance.get<ChangePlanPreview>(`${this.baseUrl}/plan/preview`, {
        params: { plan, locale }
      })
      return normalizeChangePlanPreview(response.data)
    } catch (error) {
      return handleError(error)
    }
  }

  async getSeatPreview(additionalSeats = 1, employeeId?: string): Promise<SeatChangePreview | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') || 'es'
      const params: Record<string, string | number> = { additionalSeats, locale }
      if (employeeId) {
        params.employeeId = employeeId
      }
      const response = await this.axiosInstance.get<SeatChangePreview>(`${this.baseUrl}/seats/preview`, {
        params
      })
      return normalizeSeatChangePreview(response.data)
    } catch (error) {
      return handleError(error)
    }
  }

  async updateOrigin(originCode: string | null): Promise<BillingSummary | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') || 'es'
      const response = await this.axiosInstance.put<BillingSummary>(`${this.baseUrl}/origin`, { originCode }, {
        params: { locale }
      })
      return normalizeBillingSummary(response.data)
    } catch (error) {
      return handleError(error)
    }
  }

  async confirmCheckoutSession(
    sessionId: string
  ): Promise<{ summary: BillingSummary; asyncPaymentPending: boolean } | ServiceError> {
    try {
      const locale = localStorage.getItem('appLanguage') || 'es'
      const response = await this.axiosInstance.post<{
        summary: BillingSummary
        asyncPaymentPending: boolean
      }>(`${this.baseUrl}/checkout/confirm`, {
        sessionId
      }, {
        params: { locale }
      })
      return {
        summary: normalizeBillingSummary(response.data.summary),
        asyncPaymentPending: response.data.asyncPaymentPending === true
      }
    } catch (error) {
      return handleError(error)
    }
  }

  async createCheckoutSession(
    plan: SubscriptionPlan,
    originCode: string | null,
    successUrl: string,
    cancelUrl: string
  ): Promise<{ url: string } | ServiceError> {
    try {
      const response = await this.axiosInstance.post<{ url: string }>(`${this.baseUrl}/checkout-session`, {
        plan,
        originCode,
        successUrl,
        cancelUrl
      })
      return response.data
    } catch (error) {
      return handleError(error)
    }
  }

  async createPortalSession(returnUrl: string): Promise<{ url: string } | ServiceError> {
    try {
      const response = await this.axiosInstance.post<{ url: string }>(`${this.baseUrl}/portal-session`, {
        returnUrl
      })
      return response.data
    } catch (error) {
      return handleError(error)
    }
  }
}

export const billingService = new BillingService()
